/**
 * Countries, from the ISO 3166-1 alpha-2 standard.
 *
 * This replaces two things: a five-country array typed into the settings page,
 * and 145 lines of hardcoded Singapore districts in the onboarding form that
 * assumed every customer was in one city.
 *
 * The code list is a fixed international standard, so it lives in code rather
 * than in the admin-managed reference tables. Display names come from
 * `Intl.DisplayNames`, which every supported browser ships, so the list is
 * localised into whatever language the user picked without anyone maintaining
 * a translation table.
 *
 * Cities are deliberately NOT enumerated here. There is no reliable universal
 * city dataset, so city is a free-text field.
 */

export const ISO_COUNTRY_CODES: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU',
  'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL',
  'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC',
  'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV',
  'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG',
  'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD',
  'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT',
  'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM',
  'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH',
  'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK',
  'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH',
  'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
  'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR',
  'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR',
  'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL',
  'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY',
  'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA',
  'ZM', 'ZW',
];

/**
 * Shown at the top of the country picker. These are the live markets, so most
 * users pick one of them; everyone else scrolls or types. Ordering a list is a
 * presentation choice, not a restriction — every ISO country remains
 * selectable, which is the point.
 */
export const PRIORITY_COUNTRY_CODES: readonly string[] = ['SG', 'IN', 'ID'];

export interface CountryOption {
  value: string;
  label: string;
}

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

const getDisplayNames = (locale: string): Intl.DisplayNames | null => {
  if (displayNamesCache.has(locale)) {
    return displayNamesCache.get(locale) ?? null;
  }
  let instance: Intl.DisplayNames | null = null;
  try {
    instance = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    instance = null;
  }
  displayNamesCache.set(locale, instance);
  return instance;
};

export const getCountryName = (code: string, locale = 'en'): string => {
  const displayNames = getDisplayNames(locale);
  try {
    return displayNames?.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

/**
 * Every country, localised and alphabetised, with the live markets pinned to
 * the top.
 */
export const getCountryOptions = (locale = 'en'): CountryOption[] => {
  const priority = new Set(PRIORITY_COUNTRY_CODES);

  const named = ISO_COUNTRY_CODES.map((code) => ({
    value: code,
    label: getCountryName(code, locale),
  }));

  const collator = new Intl.Collator(locale);

  const pinned = PRIORITY_COUNTRY_CODES.map((code) => ({
    value: code,
    label: getCountryName(code, locale),
  }));

  const rest = named
    .filter((option) => !priority.has(option.value))
    .sort((a, b) => collator.compare(a.label, b.label));

  return [...pinned, ...rest];
};

/** Postal/ZIP label varies by country; using the wrong word reads as careless. */
export const getPostalCodeLabelKey = (countryCode?: string): string => {
  switch (countryCode?.toUpperCase()) {
    case 'US':
      return 'accountSetup.zipCode';
    case 'IN':
      return 'accountSetup.pinCode';
    default:
      return 'accountSetup.postalCode';
  }
};
